import { toast } from 'sonner';

import { SLICE_TAG } from '@/constants';
import { toastError } from '@/utils';

import { apiSlice } from '../api-slice';
import {
    SingleSupplierResponseSchema,
    SingleSupplierResponseType,
    SupplierResponseSchema,
} from './supplier-api.schema';

export const supplierApi = apiSlice.injectEndpoints({
    endpoints: (builder) => ({
        createSupplier: builder.mutation({
            query: (data) => ({
                url: 'suppliers',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: [SLICE_TAG.SUPPLIERS],
        }),

        getSuppliers: builder.query({
            query: (queryParams) => ({
                url: 'suppliers',
                params: queryParams,
                method: 'GET',
            }),
            providesTags: [SLICE_TAG.SUPPLIERS],
            keepUnusedDataFor: 600,
            transformResponse: (response) => {
                return SupplierResponseSchema.parse(response);
            },
        }),

        getSupplierById: builder.query<SingleSupplierResponseType, string>({
            query: (id) => ({
                url: `suppliers/${id}`,
                method: 'GET',
            }),
            providesTags: (_result, _error, id) => [
                { type: SLICE_TAG.SUPPLIERS, id },
            ],
            keepUnusedDataFor: 600,
            transformResponse: (response) => {
                return SingleSupplierResponseSchema.parse(response);
            },
        }),

        updateSupplierById: builder.mutation({
            query: ({ id, ...data }) => ({
                url: `suppliers/${id}`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: [SLICE_TAG.SUPPLIERS],
        }),

        deleteSupplierById: builder.mutation({
            query: (id) => ({
                url: `suppliers/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: [SLICE_TAG.SUPPLIERS],

            async onQueryStarted(_arg, { queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    console.log('Delete supplier response:', data);
                    toast.success(
                        data?.data?.message || 'Supplier deleted successfully',
                    );
                } catch (error) {
                    toastError(error, 'supplierApi:deleteSupplierById');
                }
            },
        }),

        addSupplierPayment: builder.mutation({
            query: ({ id, ...data }) => ({
                url: `suppliers/${id}/pay`,
                method: 'PUT',
                body: {
                    amount: data.amount,
                    payment_method: data.payment_method,
                    transaction_id: data.transaction_id,
                },
            }),
            invalidatesTags: (_result, _error, arg) => [
                { type: SLICE_TAG.SUPPLIERS, id: arg.id },
            ],
        }),
    }),
});

export const {
    useCreateSupplierMutation,
    useGetSuppliersQuery,
    useGetSupplierByIdQuery,
    useLazyGetSupplierByIdQuery,
    useLazyGetSuppliersQuery,
    useUpdateSupplierByIdMutation,
    useDeleteSupplierByIdMutation,

    useAddSupplierPaymentMutation,
} = supplierApi;
